import React, { useEffect } from "react";
import { FormikProps } from "formik";
import { Grid } from "@material-ui/core";
import { IntlShape, useIntl } from "react-intl";
import { messages } from "./UserFields.messages";
import { messages as errorMessages } from "../Validation/Validation.messages";
import CardHeading from "../../../CardHeading/CardHeading";
import TextField from "../../../Fields/TextField/TextField";
import { UserRoleType } from "../../../../generated/user-api";
import FeatureController from "../../../FeatureController/FeatureController";
import { CustomerFeatureType } from "../../../../generated/customersettings-api";
import LockedTextField from "../../../Fields/LockedTextField/LockedTextField";
import { hasAnyOfRoles } from "../../../PermissionController/PermissionController";
import PhoneNumberField from "../../../Fields/PhoneNumberField/PhoneNumberField";

interface Props {
  userRole: UserRoleType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: FormikProps<any>;
  disabledFields?: string[] | undefined;
  useHsaIdLock?: boolean;
  loadHsaId?: () => void;
  usePersonNumberLock?: boolean;
  loadPersonNumber?: () => void;
  duplicateHsaIdError?: boolean;
  hideUsername?: boolean;
}

const getErrorMessage = (intl: IntlShape, error: string | undefined) => {
  if (!error || !errorMessages[error]) return "";
  return intl.formatMessage(errorMessages[error]);
};

const UserFields = (props: Props) => {
  const {
    formik,
    userRole,
    disabledFields,
    useHsaIdLock,
    loadHsaId,
    duplicateHsaIdError,
    usePersonNumberLock,
    loadPersonNumber,
    hideUsername,
  } = props;
  const intl = useIntl();
  const { errors } = formik;

  useEffect(() => {
    if (duplicateHsaIdError) {
      formik.setFieldError("hsaId", "duplicateHsaId");
    } else {
      formik.setFieldError("hsaId", undefined);
    }
  }, [duplicateHsaIdError]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Grid container spacing={2}>
      <FeatureController requiredFeature={CustomerFeatureType.UsesPersonNumber}>
        <CardHeading>
          {intl.formatMessage(messages.personalSectionTitle).toUpperCase()}
        </CardHeading>
        <Grid item xs={12}>
          <LockedTextField
            id="personNumber"
            lockedValue="************"
            placeholder="YYYYMMDDNNNN"
            loadLockedData={loadPersonNumber}
            label={intl.formatMessage(messages.personNumberLabel)}
            formik={formik}
            errorMsg={getErrorMessage(intl, errors.personNumber?.toString())}
            disableLock={!usePersonNumberLock}
            disabledFields={disabledFields}
          />
        </Grid>
      </FeatureController>

      {userRole === UserRoleType.Staff && (
        <FeatureController requiredFeature={CustomerFeatureType.UsesHsaId}>
          <Grid item xs={12}>
            <LockedTextField
              id="hsaId"
              lockedValue="*********"
              placeholder="SEXXXXXXXXXX-XXXX"
              loadLockedData={loadHsaId}
              label={intl.formatMessage(messages.hsaIdLabel)}
              formik={formik}
              errorMsg={
                duplicateHsaIdError
                  ? getErrorMessage(intl, errors.hsaId?.toString())
                  : undefined
              }
              disableLock={!useHsaIdLock}
              disabledFields={disabledFields}
            />
          </Grid>
        </FeatureController>
      )}
      {!hideUsername && (
        <Grid item xs={12}>
          <LockedTextField
            id="username"
            lockedValue="*********"
            formik={formik}
            disabledFields={["username"]}
            label={intl.formatMessage(messages.usernameLabel)}
            errorMsg={getErrorMessage(intl, errors.username?.toString())}
          />
        </Grid>
      )}

      <Grid item xs={6}>
        <TextField
          id="firstName"
          formik={formik}
          disabledFields={disabledFields}
          label={intl.formatMessage(messages.firstNameLabel)}
          errorMsg={getErrorMessage(intl, errors.firstName?.toString())}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          id="lastName"
          formik={formik}
          disabledFields={disabledFields}
          label={intl.formatMessage(messages.lastNameLabel)}
          errorMsg={getErrorMessage(intl, errors.lastName?.toString())}
        />
      </Grid>
      <Grid item xs={12}>
        <PhoneNumberField
          id="mobilePhoneNumber"
          formik={formik}
          disabledFields={disabledFields}
          label={intl.formatMessage(messages.phoneLabel)}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          id="email"
          inputMode="email"
          formik={formik}
          disabledFields={disabledFields}
          label={intl.formatMessage(messages.emailLabel)}
          errorMsg={getErrorMessage(intl, errors.email?.toString())}
        />
      </Grid>
      {/* If the displayed user has role Staff or External, show this */}
      {hasAnyOfRoles(userRole, [UserRoleType.Staff, UserRoleType.External]) && (
        <>
          <CardHeading>
            {intl.formatMessage(messages.caregiverSectionTitle).toUpperCase()}
          </CardHeading>
          <Grid item xs={12}>
            <TextField
              id="workTitle"
              formik={formik}
              disabledFields={disabledFields}
              label={intl.formatMessage(messages.workTitleLabel)}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default UserFields;
